import { useEffect, useRef, useState } from "react";
import { useAudit } from "../contexts/auditContext";
import { DocumentProcessingStatus } from "../models/audit/documentProcessingStatus";

// Cadence while Hatchet reports active processing.
const ACTIVE_POLL_INTERVAL_MS = 2000;
// Cadence while Hatchet reports terminal state. A background heartbeat runs
// continuously so we don't miss processing that starts via another path.
const HEARTBEAT_INTERVAL_MS = 10000;
// Delay before the first post-upload poll. The backend does not yet guarantee
// that Hatchet runs are registered by the time the upload POST returns, so we
// give it a short grace period to avoid an early "terminal" read.
const POST_UPLOAD_POLL_DELAY_MS = 2000;
// Upper bound on how long the spinner stays forced on after an upload when
// polls keep returning terminal. Prevents a stuck spinner if the backend
// never registers runs for some reason.
const POST_UPLOAD_FORCE_PROCESSING_MS = 12000;

/**
 * Owns the document processing status polling lifecycle for a control session.
 *
 * - Polls every 2s while the derived state is non-terminal.
 * - Falls back to a 10s heartbeat when terminal, so we catch processing that
 *   starts from another tab / device / process.
 * - When `uploadNonce` increments, schedules the next poll 2s in the future
 *   AND forces `isProcessing` to true until either a non-terminal poll
 *   arrives (backend has registered the run) or the force window expires.
 *
 * @param controlSessionId the control session to watch
 * @param uploadNonce any value that increments when the user uploads documents
 */
export function useDocumentProcessingStatus(
  controlSessionId: string | null,
  uploadNonce: number,
) {
  const { fetchDocumentProcessingStatus } = useAudit();
  const [status, setStatus] = useState<DocumentProcessingStatus | null>(null);
  const [forceProcessing, setForceProcessing] = useState(false);
  const scheduleRef = useRef<((delayMs: number) => void) | null>(null);
  const clearForceProcessingRef = useRef<(() => void) | null>(null);
  const lastHandledNonceRef = useRef<number>(uploadNonce);

  useEffect(() => {
    if (!controlSessionId) return;

    let cancelled = false;
    let timeoutId: number | null = null;

    const schedule = (delayMs: number) => {
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(tick, delayMs);
    };

    const tick = async () => {
      const result = await fetchDocumentProcessingStatus(controlSessionId);
      if (cancelled) return;
      setStatus(result);
      const state = result?.derivedState;
      // "pending" means no active runs (fresh session, or all work drained).
      // Only "processing" and "unknown" represent in-flight work.
      const idle = state !== "processing" && state !== "unknown";
      if (!idle) {
        clearForceProcessingRef.current?.();
      }
      schedule(idle ? HEARTBEAT_INTERVAL_MS : ACTIVE_POLL_INTERVAL_MS);
    };

    scheduleRef.current = schedule;
    // Initial check fires immediately.
    tick();

    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      scheduleRef.current = null;
    };
  }, [controlSessionId, fetchDocumentProcessingStatus]);

  // When the caller signals a fresh upload: flip the force flag on for an
  // immediate spinner, and reschedule the next tick 2s from now.
  useEffect(() => {
    if (uploadNonce === lastHandledNonceRef.current) return;
    lastHandledNonceRef.current = uploadNonce;
    setForceProcessing(true);
    scheduleRef.current?.(POST_UPLOAD_POLL_DELAY_MS);

    const timeoutId = window.setTimeout(() => {
      setForceProcessing(false);
    }, POST_UPLOAD_FORCE_PROCESSING_MS);
    clearForceProcessingRef.current = () => {
      window.clearTimeout(timeoutId);
      setForceProcessing(false);
      clearForceProcessingRef.current = null;
    };
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [uploadNonce]);

  const derivedState = status?.derivedState ?? null;
  const isProcessing =
    forceProcessing ||
    derivedState === "processing" ||
    derivedState === "unknown";

  return { status, isProcessing };
}
